/** What a person types into a quantity box, turned into the decimal string the server reads - Q-06.
 *
 * A US reader's natural "4,200" was a 422 (`decimal_parsing`) and the drawer said "That could not be
 * recorded." (G-Wcontrib-09). The fix is on both sides: `api/refusal.ts` now says why, and this
 * module takes the grouping comma off before the request leaves.
 *
 * **String in, string out. No `Number`, no `parseFloat`** (rule 5). A calculation input must reach
 * the server as the digits the person typed, so the only edit made here is removing a separator
 * that is *unambiguously* a thousands separator in the reader's locale. Everything else is left for
 * the server, which remains the authority, and refused here only where guessing would change the
 * quantity.
 *
 * **What is and is not a thousands separator.** The product offers two locales, en-GB and en-US
 * (`LOCALES`), and both write a decimal as a full stop and group thousands with a comma. So:
 *
 * - `4,200` and `1,234,567.5` are accepted: groups of exactly three after the first.
 * - `4.200` is **4.2** in both, never 4200. A European reader's reading of that string is the
 *   opposite number, so the separators are asked of the platform for the reader's locale
 *   (`format.numberSeparators`) and a locale that writes it the other way is read that way with no
 *   change here. A reader whose preferences have not loaded reads as the neutral language `en`.
 * - `4,2` and `1,23,456` are **refused with a sentence**, not repaired: a comma that does not sit
 *   between groups of three is either a decimal comma or a typo, and turning it into a number
 *   either way would change a figure a person has to defend. The sentence says what to type.
 */

import { numberSeparators } from "./format";

export type DecimalInput = { ok: true; value: string } | { ok: false; sentence: string };

/** The sentence for a string that cannot be read as a quantity. One wording, so every box says the
 * same thing and `refusal.ts` says it again when the server is the one refusing. */
export const NOT_A_NUMBER =
  "Type a number with digits only, such as 4200 or 4.2. A comma can only group thousands (4,200).";

function escaped(character: string): string {
  return character.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Read a typed quantity for `locale`. An empty string is `ok` and empty: whether a value is required
 * is the form's rule, not this module's. */
export function readDecimal(text: string, locale?: string): DecimalInput {
  const { decimal, group } = numberSeparators(locale);
  // Spaces (including the no-break and thin spaces a spreadsheet pastes) are never part of a number
  // here. Removing them is lossless: "4 200" cannot mean anything but 4200 to a person typing it.
  const trimmed = text.replace(/[\s\u00a0\u202f]/g, "");
  if (trimmed === "") return { ok: true, value: "" };
  const g = escaped(group);
  const d = escaped(decimal);
  // Digits grouped in threes, optionally with a fraction: the only shape whose commas are separators.
  const grouped =
    group === "" ? null : new RegExp(`^([+-]?)(\\d{1,3}(?:${g}\\d{3})+)(${d}\\d+)?$`).exec(trimmed);
  if (grouped) {
    const [, sign = "", whole = "", fraction = ""] = grouped;
    return { ok: true, value: `${sign}${whole.split(group).join("")}${fraction.replace(decimal, ".")}` };
  }
  // Anything with a separator we did not just accept is not guessed at.
  if (group !== "" && trimmed.includes(group)) return { ok: false, sentence: NOT_A_NUMBER };
  // A plain decimal (or a form the server can judge, such as 1e3) goes through untouched.
  const plain = new RegExp(`^[+-]?(\\d+(${d}\\d+)?|${d}\\d+)([eE][+-]?\\d+)?$`).test(trimmed);
  if (!plain) return { ok: false, sentence: NOT_A_NUMBER };
  return { ok: true, value: trimmed.replace(decimal, ".") };
}
